// Where in the recording the app listened, and when it would not fill in the
// point by itself.
//
// Since the whole-clip sound search, the phoneme service hears the WHOLE
// recording rather than only the stretch the clinician selected, the backend
// searches that recording for the item's sounds, and `sound_alignment` carries
// the stretch the marks came from plus the reason the model would not score the
// item automatically (eesa-backend docs/whole-clip-sound-search.md).
//
// Nothing in the frontend read that column before this, so every read here is
// tolerant: an item transcribed before the search shipped, one read by a
// phoneme service that returns no times, or one scored with the search switched
// off has no window and no reason, and both screens then look exactly as they
// always have. Absence is meaningful - it says no search ran - so nothing is
// invented to fill it.
//
// The marks are never withheld: a sound the app heard is credited wherever in
// the clip it was heard. Only the point waits for the clinician, which is why
// this has to be on the screen at all - a guard the clinician cannot see is a
// guard they cannot overrule.

// The part of the clip sent for transcription: the clinician's own selection.
export const SELECTION_COLOR = 'rgba(114, 92, 255, 0.50)';

// The read-only region drawn over the stretch the app actually scored.
// Deliberately not the selection's purple: the two regions overlap, and "what I
// chose" has to be tellable from "what the app heard".
export const HEARD_WINDOW_COLOR = 'rgba(255, 149, 0, 0.45)';

// Fixed so the region can be found again: the recorder keeps exactly one
// draggable selection region and removes the rest, and this one has to survive
// that.
export const HEARD_WINDOW_REGION_ID = 'app-heard-window';

export const HEARD_WINDOW_TITLE = 'The app heard the response here';
export const HEARD_WINDOW_LEGEND =
    'The purple band is the part of the clip that was sent for transcription.';
export const NOTHING_FOUND_NOTE =
    'The app did not find these sounds anywhere in this recording.';
export const MARKS_STAND_NOTE =
    'The sounds it heard are still marked; only the point is yours to give.';

// Why the model left the point alone. The wording says whose voice it may have
// been, because that is the decision the clinician is being asked to make.
// A Map rather than an object literal, so a reason this does not know about
// cannot reach anything through the prototype.
const WITHHELD_REASONS = new Map([
    ['heard_more_than_once', 'it heard these sounds more than once, and the first time may have been you'],
    ['outside_selection', 'it heard the response outside the part of the clip you selected'],
    ['before_clip_midpoint', 'it heard the response in the first half of the recording, which may have been your own model'],
]);

// A region narrower than this is invisible, so a single short sound would draw
// as nothing at all.
const MIN_DRAWN_SECONDS = 0.08;

const asNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;

    const number = Number(value);

    return Number.isFinite(number) ? number : null;
};

/** The stored alignment as an object, or null. The column is a JSON string. */
export const soundAlignment = (item) => {
    const raw = item?.sound_alignment;

    if (!raw) return null;

    if (typeof raw === 'object') return Array.isArray(raw) ? null : raw;

    if (typeof raw !== 'string') return null;

    try {
        const parsed = JSON.parse(raw);

        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
    } catch {
        return null;
    }
};

/**
 * True when the search ran and found none of the item's sounds anywhere in the
 * recording. The backend then records the whole clip as the window, because
 * there is no response to locate and picking a stretch would be inventing one -
 * so there is nothing to draw, and saying "the app heard the response here"
 * across the whole waveform would be a lie.
 */
export const foundNothing = (item) => {
    const test = soundAlignment(item)?.occurrence_test;

    return test === 'nothing_matched' || test === 'nothing_heard';
};

/**
 * The stretch of the recording the marks came from, in seconds from the start
 * of the clip, or null when no search ran or there is nothing to draw.
 */
export const heardWindow = (item) => {
    if (foundNothing(item)) return null;

    const window = soundAlignment(item)?.window;

    if (!window || typeof window !== 'object') return null;

    const start = asNumber(window.start);
    const end = asNumber(window.end);

    if (start === null || end === null || end < start) return null;

    return { start, end };
};

/**
 * The same window, clamped to the clip and widened to something a person can
 * see, ready to hand to wavesurfer. `duration` is the decoded clip length.
 */
export const heardRegion = (item, duration = null) => {
    const window = heardWindow(item);

    if (!window) return null;

    const clip = asNumber(duration);
    const start = Math.max(0, window.start);
    let end = Math.max(window.end, start + MIN_DRAWN_SECONDS);

    if (clip !== null && clip > 0) {
        if (start >= clip) return null;

        end = Math.min(end, clip);
    }

    return end > start ? { start, end } : null;
};

/**
 * "The app heard the response here (2.1s to 2.6s)", or null.
 *
 * A window that fell outside the part of the clip that was sent says so: that
 * is the under-credit case the selection used to cause, and it is worth seeing
 * whether or not it also cost the item its point.
 */
export const heardWindowNote = (item) => {
    const window = heardWindow(item);

    if (!window) return null;

    const outside = soundAlignment(item)?.inside_selection === false
        ? ', outside the part of the clip you sent'
        : '';

    return `${HEARD_WINDOW_TITLE} (${window.start.toFixed(1)}s to ${window.end.toFixed(1)}s)${outside}.`;
};

/**
 * Which rule withheld the automatic point, or null.
 *
 * The reason is recorded whatever the marks came to, so it only actually cost
 * the item a point when the model was going to give one: `ai_score` is that
 * suggestion, kept apart from `score`, which is the clinician's.
 */
export const creditWithheldReason = (item) => {
    const reason = soundAlignment(item)?.credit_withheld_reason;

    if (typeof reason !== 'string' || !WITHHELD_REASONS.has(reason)) return null;

    return asNumber(item?.ai_score) === 1 ? reason : null;
};

/** The one-line explanation to show by the score, or null. */
export const creditWithheldMessage = (item) => {
    const reason = creditWithheldReason(item);

    if (!reason) return null;

    const occurrences = asNumber(soundAlignment(item)?.occurrences);
    const why = reason === 'heard_more_than_once' && occurrences !== null && occurrences > 2
        ? `it heard these sounds ${occurrences} times in this recording, and the first time may have been you`
        : WITHHELD_REASONS.get(reason);

    // Only ask for a score when there is not one already.
    const ask = asNumber(item?.score) === null ? ' Score this one yourself.' : '';

    return `The app did not give this item the point by itself: ${why}.${ask}`;
};
